import { useNavigate } from 'react-router-dom';
import { CalendarDays } from 'lucide-react';

import type { CandidateRead } from '@/api/schema';
import { Panel } from '@/components/layout/Page';
import { Button } from '@/components/ui/Button';
import { ErrorState } from '@/components/ui/EmptyState';
import { Spinner } from '@/components/ui/Spinner';
import { useCurrentUser } from '@/features/auth/api';
import { useDateFormat } from '@/features/company/api';
import { formatDateTime } from '@/lib/dates';
import { can } from '@/lib/permissions';

import { useCandidateMeetings } from '../api';
import { MEETING_STATUS_LABEL } from '../labels';
import styles from './CandidateMeetings.module.css';

export function CandidateMeetings({ candidate }: { candidate: CandidateRead }) {
  const { data: user } = useCurrentUser();
  const meetings = useCandidateMeetings(candidate.id);
  const dateFormat = useDateFormat();
  const navigate = useNavigate();
  return (
    <Panel title="Meetings" icon={<CalendarDays size={15} />}>
      {can(user, 'meetings', 'create') ? (
        <Button
          size="sm"
          onClick={() => {
            void navigate(`/meetings/new?candidate=${candidate.id}`);
          }}
        >
          Schedule a meeting
        </Button>
      ) : null}
      {meetings.isPending ? (
        <Spinner label="Loading meetings" />
      ) : meetings.isError ? (
        <ErrorState
          message="Meetings could not be loaded."
          onRetry={() => {
            void meetings.refetch();
          }}
        />
      ) : meetings.data.length === 0 ? (
        <p>No meetings scheduled for this candidate.</p>
      ) : (
        <ul className={styles.list}>
          {meetings.data.map((meeting) => (
            <li key={meeting.id}>
              <div>
                <b>{meeting.title}</b>
                <p>
                  {formatDateTime(meeting.scheduled_at, dateFormat)} ·{' '}
                  {MEETING_STATUS_LABEL[meeting.status]}
                </p>
              </div>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => {
                  void navigate(`/meetings/${meeting.id}/edit?candidate=${candidate.id}`);
                }}
              >
                View meeting
              </Button>
            </li>
          ))}
        </ul>
      )}
    </Panel>
  );
}
